/**
 * @namespace pfKioskServices
 */

/**
 * The Schedule Object.
 * @typedef {Object} Schedule
 * @type {object}
 * @global
 *
 * @property {string} ScheduleGuid
 * @property {string} AmenityEquipmentGuid
 * @property {string} StartTimeDT
 * @property {string} StartTime
 * @property {?string} EndTimeDT
 * @property {string} EndTime
 * @property {string} MemberGuid
 * @property {Member} Member
 * @property {string} ClubCode
 * @property {boolean} Activated
 * @property {string} AmenityCategoryId
 * @property {string} AmenityTypeId
 * @property {number} MinutesSincePrevious
 * @property {Object} ActivationEvent
 * @property {Object} CompletionEvent
 */

/**
 * The Member Object.
 * @typedef {Object} Member
 * @type {object}
 * @global
 *
 * @property {string}         FirstName
 * @property {string}         LastName
 * @property {string}         City
 * @property {string}         StateCode
 * @property {string}         PostalCode
 * @property {string}         CountryCode
 * @property {string}         Email
 * @property {string}         BirthDate
 * @property {string}         Gender
 * @property {string}         PhoneNumber
 * @property {boolean}        HasSuitableCredit
 * @property {string}         CellPhoneProvider
 * @property {?boolean}       InGoodStanding
 * @property {?boolean}       Active
 * @property {string}         Guid
 * @property {string}         Status
 * @property {string}         LastUpdate
 * @property {string}         ClubCode
 * @property {string}         MemberBarcode
 * @property {string}         AgreementNumber
 * @property {string}         Tag
 * @property {string}         MemberType
 * @property {Object}         SignedWaivers
 */

var pfKioskServices = {

  /**
   * Get all schedules for given equipment between times provided
   * @param {string}      startTime   requested start time
   * @param {string}      endTime     requested end time
   * @param {object}      equipment   array of equipment
   * @param {function}    onSuccess   xhr success
   * @param {function}    onFail      xhr fail
   */
  getSchedules: function (startTime, endTime, equipment, onSuccess, onFail) {
    common.setLoader(pfFetchTranslation('siteLoad.existingSchedules'), null);

    var url = config.REST_HOST + '/SchedulesForPageLoad';
    $.ajax({
      method: 'GET',
      headers: {
        Accept: 'application/json; charset=utf-8'
      },
      retry: {
        interval: 30000,
        maximum: 60000
      },
      data: {
        'clubs': common.configData.clubCode,
        'club': common.configData.clubCode,
        'startTime': startTime,
        'endTime': endTime,
        'resources': equipment.toString()
      },
      url: url,
      success: function (data) {
        onSuccess(data);
      },
      error: function (jqXHR, textStatus, errorThrown) {
        onFail(jqXHR, textStatus, errorThrown);
      }
    });
  },

  /**
   * Get all available amenities from given array in the given timeframe
   * @param {object}      amenities   array of amenity guids
   * @param {string}      startTime   requested time slot start time
   * @param {string}      endTime     requested time slot end time
   * @param {object}      selector    element
   * @param {function}    onSuccess   xhr success
   * @param {function}    onFail      xhr fail
   */
  quickUpdate: function (amenities, startTime, endTime, selector, onSuccess, onFail) {
    common.setLoader(pfFetchTranslation('siteLoad.checkingAvailability'));
    $.ajax({
      cache: false,
      method: 'GET',
      headers: {
        Accept: 'application/json; charset=utf-8'
      },
      data: {
        'resources': amenities,
        'startTime': startTime,
        'endTime': endTime
      },
      url: config.REST_HOST + '/QuickUpdate',
      success: function (data) {
        onSuccess(data, selector);
      },
      error: function (jqXHR, textStatus, errorThrown) {
        onFail(jqXHR, textStatus, errorThrown);
      }
    });
  },

  /**
   *
   * @param {object}      data
   * @param {function}    onSuccess   xhr success
   * @param {function}    onFail      xhr fail
   */
  updateMemberData: function (data, onSuccess, onFail) {
    common.setLoader(pfFetchTranslation('siteLoad.gettingUpdatedInfo'));
    $.ajax({
      method: 'PUT',
      dataType: 'json',
      contentType: 'application/json; charset=utf-8',
      data: JSON.stringify(data),
      url: config.REST_HOST + '/Members/' + data.Guid,
      success: function (data) {
        onSuccess(data);
      },
      error: function (jqXHR, textStatus, errorThrown) {
        onFail(jqXHR, textStatus, errorThrown);
      }
    });
  },

  /**
   * Get Schedule data from KioskData and Member Services
   * @param {object}      data
   * @param {boolean}     memberCache   last member info
   * @param {boolean}     scheduleCache last cached schedule
   * @param {boolean}     needIndex     get progression data?
   * @param {function}    onSuccess     xhr success
   * @param {function}    onFail        xhr fail
   */
  getSchedule: function (data, scheduleCache, memberCache, needIndex, onSuccess, onFail) {
    common.setLoader(pfFetchTranslation('siteLoad.gettingScheduleInfo'));

    function retry(request, deferred, error) {
      request.retryCount++;
      if (
        (request.retryCount <= request.retryLimit)
      ) {
        // retry
        setTimeout(function () {
          $.ajax(request);
        }, 2500);
      } else {
        error.url = request.url;
        return deferred.reject(error);
      }
    }

    function getMember() {
      return $.Deferred(function (deferred) {
        if (memberCache && memberCache.guid === data.memberGuid) {
          return deferred.resolve(memberCache);
        }

        $.ajax({
          method: 'GET',
          headers: {
            Accept: 'application/json; charset=utf-8'
          },
          data: {
            'requestingClub': common.configData.clubCode,
            'memberGuid': data.memberGuid
          },
          url: config.MEMBER_SERVICE + '/Member/Id',
          retryCount: 0,
          retryLimit: 5,
          success: function (res, textStatus, jqXHR) {
            var member = common.getNestedObject(res, ['members', 0]);
            if (!member) {
              return retry(this, deferred, {
                data: data,
                response: res,
                message: pfFetchTranslation('siteLoad.gettingUpdatedInfoError')
              });
            }
            return deferred.resolve(member);
          },
          error: function (xhr, textStatus, errorThrown) {
            return retry(this, deferred, {
              data: data,
              response: errorThrown,
              message: pfFetchTranslation('siteLoad.gettingUpdatedInfoError')
            });
          }
        });
      }).promise();
    }

    function getSchedule() {
      return $.Deferred(function (deferred) {
        if (scheduleCache && scheduleCache.ScheduleGuid === data.scheduleGuid) {
          return deferred.resolve(scheduleCache);
        }

        $.ajax({
          method: 'GET',
          contentType: 'application/json; charset=utf-8',
          url: config.REST_HOST + '/Schedules/' + data.scheduleGuid,
          retryCount: 0,
          retryLimit: 5,
          success: function (res, textStatus, jqXHR) {
            var schedule = common.getNestedObject(res, ['Data']);
            if (!schedule) {
              return retry(this, deferred, {
                data: JSON.stringify(data),
                response: JSON.stringify(res),
                message: pfFetchTranslation('siteLoad.gettingScheduleInfoError')
              });
            }
            return deferred.resolve(schedule);
          },
          error: function (xhr, textStatus, errorThrown) {
            return retry(this, deferred, {
              data: JSON.stringify(data),
              response: errorThrown,
              message: pfFetchTranslation('siteLoad.gettingScheduleInfoError')
            });
          }
        });
      }).promise();
    }

    function getProgressionInfo() {
      return $.Deferred(function (deferred) {
        $.ajax({
          method: 'GET',
          headers: {
            Accept: 'application/json; charset=utf-8'
          },
          data: {
            'memberGuid': data.memberGuid,
            'categoryGuid': data.amenityCategoryGuid
          },
          url: config.MEMBER_SERVICE + '/Member/ProgressionInfo',
          retryCount: 0,
          retryLimit: 5,
          success: function (res, textStatus, jqXHR) {
            var progressionIndex = common.getNestedObject(res, ['memberProgressions', 0, data.amenityCategoryGuid, 'value']);
            if (progressionIndex === undefined || progressionIndex === null) {
              return retry(this, deferred, {
                data: data,
                response: res,
                message: pfFetchTranslation('siteLoad.gettingTanningInfoError')
              });
            }
            return deferred.resolve(progressionIndex);
          },
          error: function (xhr, textStatus, errorThrown) {
            return retry(this, deferred, {
              data: data,
              response: errorThrown,
              message: pfFetchTranslation('siteLoad.gettingTanningInfoError')
            });
          }
        });
      }).promise();
    }

    var queue = [getSchedule(), getMember()];

    if (needIndex) {
      queue.push(getProgressionInfo());
    }

    $.when.apply(null, queue)
      .then(
        function success(scheduleData, memberData, progression) {
          pfKioskAdmin.scheduleCache = scheduleData;
          pfKioskAdmin.memberCache = memberData;

          onSuccess(scheduleData, memberData, progression);
        },
        function fail(err) {
          (typeof onFail === 'function') ? onFail(err): common.showError(null, null, err.message);
          pfKioskServices.trackEvent({
            name: 'Log',
            properties: {
              type: 'Error',
              message: err.message,
              method: 'getschedule',
              url: err.url,
              data: err.data,
              response: err.response
            }
          });
        });

  },

  /**
   * Get A list of member schedules that intersect with the given start and end time
   * @param {string}      abcGUID     member guid
   * @param {string}      startTime   requested start time
   * @param {string}      endTime     requested end time
   * @param {string}      categories  request schedules for a specific category
   * @param {string}      type        request schedules for a specific amenity type
   * @param {string}      resources   request schedules for specific resources
   * @param {string}      club        request schedules for a specific club
   * @param {function}    onSuccess   xhr success
   * @param {function}    onFail      xhr fail
   */
  getMemberSchedules: function (abcGUID, startTime, endTime, categories, type, resources, club, onSuccess, onFail) {
    common.setLoader(pfFetchTranslation('siteLoad.checkingMemberSchedules'));

    var query = {
      'startTime': startTime,
      'endTime': endTime
    };
    if (categories) {
      query.categories = categories;
    }

    if (type) {
      query.types = type;
    }

    if (resources) {
      query.resources = resources;
    }

    $.ajax({
      method: 'GET',
      headers: {
        Accept: 'application/json; charset=utf-8'
      },
      data: query,
      attempts: 1,
      url: config.REST_HOST + '/Members/' + abcGUID + '/Schedules',
      success: function (data) {
        var self = this;

        if (!data || (data.Status === 0 && data.Data === null) && self.attempts > 0) {
          setTimeout(function () {
            self.attempts--;
            $.ajax(self);
          }, 5000);
        } else if (!data || (!self.attempts && (data.Status === 0 && data.Data === null))) {
          onFail(null, null, pfFetchTranslation('siteLoad.retrievingScheduleHistoryError'));
        } else {
          onSuccess(data);
        }
      },
      error: function (jqXHR, textStatus, errorThrown) {
        onFail(jqXHR, textStatus, errorThrown);
      }
    });
  },

  /**
   * Posts a member schedule
   * @param {object}      data        schedule data
   * @param {string}      kioskType   type of kiosk request was made from
   * @param {function}    onSuccess   xhr success
   * @param {function}    onFail      xhr fail
   */
  postMemberSchedule: function (data, kioskType, onSuccess, onFail) {

    common.setLoader(pfFetchTranslation('siteLoad.creatingMemberSchedule'));
    $.ajax({
      method: 'POST',
      data: JSON.stringify(data),
      url: config.REST_HOST + '/Schedules',
      dataType: 'json',
      contentType: 'application/json; charset=utf-8',
      headers: {
        'Location': kioskType,
        Accept: 'application/json; charset=utf-8'
      },
      success: function (schedule) {
        if (!schedule.Data) {
          onFail(null, null, schedule.Message);
          pfKioskServices.trackEvent({
            name: 'Schedule Failed',
            properties: {
              kiosk: kioskType,
              equipmentGuid: data.AmenityEquipmentGuid,
              memberGuid: data.MemberGuid,
              message: schedule.Message,
              amenityCategoryId: data.AmenityCategoryId,
              startTime: data.startTime,
              endTime: data.endTime
            }
          });
        } else {
          onSuccess(schedule);
          pfKioskServices.trackEvent({
            name: 'Schedule Created',
            properties: {
              kiosk: kioskType,
              equipmentGuid: schedule.Data.AmenityEquipmentGuid,
              memberGuid: schedule.Data.MemberGuid,
              scheduleGuid: schedule.Data.ScheduleGuid,
              amenityCategoryId: data.AmenityCategoryId
            }
          });
        }
      },
      error: function (jqXHR, textStatus, errorThrown) {
        onFail(jqXHR, textStatus, errorThrown);
      }
    });
  },

  /**
   * Request to remove a member schedule
   * @param {string}      scheduleGuId    schedule guid to remove
   * @param {function}    onSuccess       xhr success
   * @param {function}    onFail          xhr fail
   */
  deleteMemberSchedule: function (scheduleGuId, onSuccess, onFail) {
    common.setLoader(pfFetchTranslation('siteLoad.removingMemberSchedule'));
    $.ajax({
      method: 'DELETE',
      dataType: 'json',
      contentType: 'application/json; charset=utf-8',
      url: config.REST_HOST + '/Schedules/' + scheduleGuId,
      success: function (data) {
        onSuccess(data, scheduleGuId);
        pfKioskServices.trackEvent({
          name: 'Schedule Canceled',
          properties: {
            scheduleGuid: scheduleGuId
          }
        });
      },
      error: function (jqXHR, textStatus, errorThrown) {
        onFail(jqXHR, textStatus, errorThrown);
      }
    });
  },

  /**
   * Updates a member schedule
   * @param {object}      data        new schedule data
   * @param {function}    onSuccess   xhr success
   * @param {function}    onFail      xhr fail
   */
  updateMemberSchedule: function (data, onSuccess, onFail) {
    common.setLoader(pfFetchTranslation('siteLoad.updatingMemberSchedule'));
    $.ajax({
      method: 'PUT',
      data: JSON.stringify(data),
      dataType: 'json',
      contentType: 'application/json; charset=utf-8',
      url: config.REST_HOST + '/Schedules/' + data.ScheduleGuid,
      success: function (res) {
        onSuccess(res);
        pfKioskServices.trackEvent({
          name: 'Schedule Updated',
          properties: {
            scheduleGuid: res.Data.ScheduleGuid,
            memberGuid: res.Data.MemberGuid
          }
        });
      },
      error: function (jqXHR, textStatus, errorThrown) {
        onFail(jqXHR, textStatus, errorThrown);
      }
    });
  },

  /**
   * Gets the current status of the given equipment tag from the local service
   * @param {string}      service             local service identifier
   * @param {string}      tag                 equipment tag identifier
   * @param {string}      schedProcedureID    schedule guid
   * @param {function}    onSuccess           xhr success
   * @param {function}    onFail              xhr fail
   * @param {boolean}     checkService        fallback to check the service endpoint in case the initial request fails
   */
  getChairStatus: function (service, tag, schedProcedureID, onSuccess, onFail, checkService) {
    common.setLoader(pfFetchTranslation('siteLoad.checkingAmenityStatus'));

    checkService = (checkService !== false);
    var serviceUrl = '';
    if (service.toUpperCase() === 'WIFITAGWCFSERVICE') {
      serviceUrl = common.configData.wifiTagServiceUrl + '/' + service;
    } else if (service.toUpperCase() === 'ADNETWCFSERVICE') {
      serviceUrl = common.configData.adnetServiceUrl + '/' + service;
    } else {
      // default to WiFiTagWCFService
      service = 'WiFiTagWCFService';
      serviceUrl = common.configData.wifiTagServiceUrl + '/' + service;
    }
    $.ajax({
      cache: false,
      method: 'GET',
      timeout: 15000,
      headers: {
        Accept: 'application/json; charset=utf-8'
      },
      url: serviceUrl + '/TagData/' + tag + ',' + schedProcedureID,
      beforeSend: function (jqXHR, settings) {
        common.checkMixedContent(settings.url, onFail);
      },
      success: function (data) {
        var response;
        if (service.toUpperCase() === 'ADNETWCFSERVICE') {
          response = data.GetDeviceInfoResult || data.GetTagInfoResult;
        } else {
          response = data.GetWiFiTagInfoResult;
        }
        onSuccess(response);
      },
      error: function (jqXHR, textStatus, errorThrown) {
        if (checkService) {
          pfKioskServices.serviceCheck(service, tag, schedProcedureID, onSuccess, onFail);
        } else {
          onFail(jqXHR, textStatus, errorThrown);
        }
      }
    });
  },

  getAllTagData: function (onSuccess, onFail) {
    common.setLoader(pfFetchTranslation('siteLoad.checkingEquipmentStatus'));
    var queue = [];

    function getChairStatus(statusId) {
      for (var status in pfKioskAdmin.chairStatus) {
        if (pfKioskAdmin.chairStatus.hasOwnProperty(status) && pfKioskAdmin.chairStatus[status] === statusId) {
          return status;
        }
      }
    }

    if (common.configData.wifiTagServiceUrl) {
      queue.push(
        $.ajax({
          method: 'GET',
          timeout: 15000,
          headers: {
            Accept: 'application/json; charset=utf-8'
          },
          url: common.configData.wifiTagServiceUrl + '/WIFITAGWCFSERVICE/allTagData/allWIFIData'
        })
      );
    }
    if (common.configData.adnetServiceUrl) {
      queue.push(
        $.ajax({
          method: 'GET',
          timeout: 15000,
          headers: {
            Accept: 'application/json; charset=utf-8'
          },
          url: common.configData.adnetServiceUrl + '/ADNETWCFSERVICE/allTagData/allADNETData'
        })
      );
    }
    $.when.apply($, queue)
      .then(
        function success() {
          var allTagData = [];

          for (var a = 0; a < arguments.length; a++) {
            var prop = arguments[a][0];
            if (prop.hasOwnProperty('GetAllTagInfoResult')) {
              prop['GetAllTagInfoResult'].map(function (tagData) {
                tagData.Service = 'ADNETWCFService';
                allTagData.push(tagData);
              });
            } else if (prop.hasOwnProperty('GetAllWiFiTagInfoResult')) {
              prop['GetAllWiFiTagInfoResult'].map(function (tagData) {
                tagData.Service = 'WiFiTagWCFService';
                allTagData.push(tagData);
              });
            }
          }

          $('.box.scheduled').removeClass(function (index, className) {
            if (className.indexOf('ended') === -1 && className.indexOf('state-complete') === -1) {
              return (className.match(/(^|\s)state-\S+/g) || []).join(' ');
            }
          });
          for (var i = 0; i < allTagData.length; i++) {
            var data = allTagData[i];
            var tagStatus = getChairStatus(data.Status);

            $('.box[data-tagid=\'' + data.TagID + '\'][data-service=\'' + data.Service + '\'][data-scheduleguid=\'' + data.ScheduledProcedureGUID + '\']')
              .removeClass(function (index, className) {
                return (className.match(/(^|\s)state-\S+/g) || []).join(' ');
              })
              .addClass('state-' + tagStatus);

            var nowUnix = moment().unix();
            var delayUnix = data.TimeDelayLeftSecs;
            var timeLeftUnix = data.TimeLeftSecs;
            var totalRunTime = (nowUnix + delayUnix + timeLeftUnix) * 1000;

            $('.box[data-tagid=\'' + data.TagID + '\'][data-service=\'' + data.Service + '\'][data-scheduleguid=\'' + data.ScheduledProcedureGUID + '\']')
              .attr('data-runtime', totalRunTime);

          }

        },
        function fail(err, err2, err3) {
          if (typeof onFail === 'function') {
            onFail();
          }
        }
      );
  },

  /**
   * Endpoint to create the local service if the status check fails
   * @param {string}      service             local service identifier
   * @param {string}      tag                 equipment tag identifier
   * @param {string}      schedProcedureID    schedule guid
   * @param {function}    onSuccess           xhr success
   * @param {function}    onFail              xhr fail
   */
  serviceCheck: function (service, tag, schedProcedureID, onSuccess, onFail) {
    var serviceUrl = '';
    if (service.toUpperCase() === 'WIFITAGWCFSERVICE') {
      serviceUrl = common.configData.wifiTagServiceUrl + '/' + service;
    } else if (service.toUpperCase() === 'ADNETWCFSERVICE') {
      serviceUrl = common.configData.adnetServiceUrl + '/' + service;
    } else {
      // default to WiFiTagWCFService
      service = 'WiFiTagWCFService';
      serviceUrl = common.configData.wifiTagServiceUrl + '/' + service;
    }
    $.ajax({
      cache: false,
      method: 'GET',
      timeout: 5000,
      headers: {
        Accept: 'application/json; charset=utf-8'
      },
      url: serviceUrl,
      success: function () {
        pfKioskServices.getChairStatus(service, tag, schedProcedureID, onSuccess, onFail, false);
      },
      error: function (jqXHR, textStatus, errorThrown) {
        onFail(jqXHR, textStatus, errorThrown);
      }
    });
  },

  /**
   * Gets the content of a waiver by waiver title
   * @param {string}      title       waiver title
   * @param {function}    onSuccess   xhr success
   * @param {function}    onFail      xhr fail
   */
  getWaiver: function (title, onSuccess, onFail) {
    common.setLoader(pfFetchTranslation('siteLoad.checkingWaivers'));
    var lang = langLookup();
    $.ajax({
      cache: false,
      method: 'GET',
      headers: {
        Accept: 'application/json; charset=utf-8'
      },
      url: config.CLUB_SERVICE + '/Waivers/WaiverRevisions/' + title + '?lang=' + langLookup() + '&language=' + lang,
      success: function (data) {
        onSuccess(data);
      },
      error: function (jqXHR, textStatus, errorThrown) {
        onFail(jqXHR, textStatus, errorThrown);
      }
    });
  },

  /**
   * Gets the required waiver by type for the given club
   * @param {string}      type        waiver type
   * @param {boolean}     retry       retry ajax call?
   * @param {function}    onSuccess   xhr success
   * @param {function}    onFail      xhr fail
   */
  getWaiverTemplate: function (type, retry, onSuccess, onFail) {
    var lang = langLookup();

    if (retry) {
      retry = {
        interval: 1875,
        maximum: 60000
      };
    }

    var params = '?WaiverType=' + type + '&ClubId=' + common.configData.clubCode + '&state=' + common.configData.state + '&language=' + lang;
    common.setLoader(pfFetchTranslation('siteLoad.checkingWaivers'));
    $.ajax({
      cache: false,
      method: 'GET',
      retry: retry,
      headers: {
        Accept: 'application/json; charset=utf-8'
      },
      url: config.CLUB_SERVICE + '/Waivers/Current?WaiverType=' + type + '&ClubId=' + common.configData.clubCode + '&state=' + common.configData.state,
      success: function (data) {
        onSuccess(data);
      },
      error: function (jqXHR, textStatus, errorThrown) {
        onFail(jqXHR, textStatus, errorThrown);
      }
    });
  },

  /**
   * Posts to put an amenity equipment into maintenance mode
   * @param {string}      amenityEquipmentGuid    amenity equipment guid
   * @param {string}      kioskType               string describing request origin
   * @param {function}    onSuccess               xhr success
   * @param {function}    onFail                  xhr fail
   */
  postAmenityBlock: function (amenityEquipmentGuid, kioskType, onSuccess, onFail) {
    common.setLoader(pfFetchTranslation('siteLoad.settingAmenityMaintenance'));

    var amenityCategory = common.getAmenityType(amenityEquipmentGuid);
    $.ajax({
      method: 'POST',
      data: JSON.stringify({
        'AmenityEquipmentGuid': amenityEquipmentGuid,
        'AmenityCategoryId': amenityCategory ? amenityCategory.id : null,
        'ClubCode': common.configData.clubCode,
        'StartTime': moment().format(common.dateFormat)
      }),
      dataType: 'json',
      contentType: 'application/json; charset=utf-8',
      headers: {
        'Location': kioskType,
        Accept: 'application/json; charset=utf-8'
      },
      url: config.REST_HOST + '/Schedules/force',
      success: function (data) {
        onSuccess(data);
      },
      error: function (jqXHR, textStatus, errorThrown) {
        onFail(jqXHR, textStatus, errorThrown || pfFetchTranslation('siteLoad.processingError'));
      }
    });
  },

  /**
   * Post to submit a note for an amenity equipment maintenance
   * @param {string}      amenityEquipmentGuid    amenity equipment guid
   * @param {string}      notes                   notes to be added on request
   * @param {function}    onSuccess               xhr success
   * @param {function}    onFail                  xhr fail
   */
  submitNote: function (amenityEquipmentGuid, notes, onSuccess, onFail) {
    $.ajax({
      method: 'POST',
      data: JSON.stringify({
        'Content': notes,
        'ResourceGuid': amenityEquipmentGuid
      }),
      dataType: 'json',
      contentType: 'application/json; charset=utf-8',
      headers: {
        Accept: 'application/json; charset=utf-8'
      },
      url: config.REST_HOST + '/Notes',
      success: function (data) {
        onSuccess(data);
      },
      error: function (jqXHR, textStatus, errorThrown) {
        onFail(jqXHR, textStatus, errorThrown);
      }
    });
  },

  /**
   * Post to remove an amenity from maintenance
   * @param {string}      scheduleGuId    schedule guid
   * @param {function}    onSuccess       xhr success
   * @param {function}    onFail          xhr fail
   */
  postAmenityUnblock: function (scheduleGuId, onSuccess, onFail) {
    common.setLoader(pfFetchTranslation('siteLoad.removingAmenityMaintenance'));
    $.ajax({
      method: 'DELETE',
      dataType: 'json',
      contentType: 'application/json; charset=utf-8',
      url: config.REST_HOST + '/Schedules/' + scheduleGuId,
      success: function (data) {
        onSuccess(data);
      },
      error: function (jqXHR, textStatus, errorThrown) {
        onFail(jqXHR, textStatus, errorThrown);
      }
    });
  },

  /**
   *
   * @param {string}      antennaId
   * @param {string}      memberGuid
   * @param {string}      readerId
   * @param {function}    onSuccess   xhr success
   * @param {function}    onFail      xhr fail
   */
  readerEvent: function (antennaId, memberGuid, readerId, onSuccess, onFail) {
    $.ajax({
      method: 'POST',
      data: JSON.stringify({
        'AntennaId': antennaId,
        'ClubCode': common.configData.clubCode,
        'MemberIdentifier': memberGuid,
        'ReaderId': readerId
      }),
      dataType: 'json',
      contentType: 'application/json; charset=utf-8',
      url: config.REST_HOST + '/ReaderEvents',
      success: function (data) {
        if (onSuccess) {
          onSuccess(data);
        }
      },
      error: function (jqXHR, textStatus, errorThrown) {
        if (onFail) {
          onFail(jqXHR, textStatus, errorThrown);
        }
      }
    });
  },

  /**
   * Get a full club reports
   * @param {string}      reportPath        report type
   * @param {function}    onSuccess   xhr success
   * @param {function}    onFail      xhr fail
   */
  getClubReport: function (reportPath, onSuccess, onFail) {
    $.ajax({
      cache: false,
      method: 'GET',
      timeout: 120000,
      headers: {
        Accept: 'application/json; charset=utf-8'
      },
      url: config.REPORT_HOST + '/' + reportPath + '?lang=' + langLookup('alt') + '&club=' + common.configData.clubCode,
      success: function (data) {
        onSuccess(data);
      },
      error: function (jqXHR, textStatus, errorThrown) {
        onFail(jqXHR, textStatus, errorThrown);
      }
    });
  },

  /**
   * Get individual member report
   * @param {string}      memberGuid
   * @param {function}    onSuccess   xhr success
   * @param {function}    onFail      xhr fail
   */
  getMemberReport: function (memberGuid, onSuccess, onFail) {
    $.ajax({
      cache: false,
      method: 'GET',
      timeout: 120000,
      headers: {
        Accept: 'application/json; charset=utf-8'
      },
      url: config.REPORT_HOST + '/member-usage?resources[]=' + memberGuid + '&startTime=' + moment().subtract(7, 'd').format() + '&endTime=' + moment().format() + '&timeZone=' + common.configData.timeZoneId + '&lang=' + langLookup(),
      success: function (data) {
        onSuccess(data);
      },
      error: function (jqXHR, textStatus, errorThrown) {
        onFail(jqXHR, textStatus, errorThrown);
      }
    });
  },

  /**
   * Get individual member tanning report
   * @param {string}      memberGuid
   * @param {function}    onSuccess   xhr success
   * @param {function}    onFail      xhr fail
   */
  getTanningReport: function (memberGuid, onSuccess, onFail) {
    // Get the root level amenity category id's for this environment/slot.
    var rootAmenityCategoryIds = null;
    if (common.configData.amenities) {
      var tanning = Enumerable
                      .asEnumerable(common.configData.amenities)
                      .Where(n => n.name.startsWith("Tanning"))
                      .FirstOrDefault();
      if (tanning) {
        rootAmenityCategoryIds = tanning.categoryId;
      }
    }
    else {
      onFail("Can not locate tanning category Id.");
    }
    // Read the tanning history report window in months
    var tanningHistoryMonths = common.getConfigValue('tanningHistoryMonths');
    // If it's not set, read directly from the environment variable for default value.
    if (!tanningHistoryMonths) {
      tanningHistoryMonths = parseInt(config.TANNING_HISTORY_MONTHS);
    }
    var fullUrl = config.REPORT_HOST + '/member-usage?resources[]=' + memberGuid +
    '&amenityCategoryIds=' + rootAmenityCategoryIds + 
    '&startTime=' + moment().subtract(tanningHistoryMonths, 'M').format() + '&endTime=' + moment().format() +
    '&timeZone=' + common.configData.timeZoneId + '&lang=' + langLookup() + '&filePrefix=tanning-usage';

    $.ajax({
      cache: false,
      method: 'GET',
      timeout: 120000,
      headers: {
        Accept: 'application/json; charset=utf-8'
      },
      url: fullUrl,
      success: function (data) {
        onSuccess(data);
      },
      error: function (jqXHR, textStatus, errorThrown) {
        onFail(jqXHR, textStatus, errorThrown);
      }
    });
  },

  /**
   * Gets the members signed waiver pdf link
   * @param {string}      memberGuid  member guid string
   * @param {string}      waiverGuid  waiver guid string
   * @param {function}    onSuccess   xhr success callback
   * @param {function}    onFail      xhr fail callback
   */
  getWaiverPDF: function (memberGuid, waiverGuid, onSuccess, onFail) {
    $.ajax({
      cache: false,
      method: 'GET',
      headers: {
        Accept: 'application/json; charset=utf-8'
      },
      timeout: 120000,
      url: config.REPORT_HOST + '/waiver?memberId=' + memberGuid + '&waiverId=' + waiverGuid + '&lang=' + langLookup('alt'),
      success: function (data) {
        onSuccess(data);
      },
      error: function (jqXHR, textStatus, errorThrown) {
        if (errorThrown === '' || errorThrown === null || errorThrown === undefined) {
          errorThrown = pfFetchTranslation('siteLoad.generatingReportError');
        }
        onFail(jqXHR, textStatus, errorThrown);
      }
    });
  },

  getConfig: function (kioskId, onSuccess, onFail) {
    $.ajax({
      retry: {
        interval: 1875,
        maximum: 60000
      },
      method: 'GET',
      headers: {
        Accept: 'application/json; charset=utf-8'
      },
      url: config.CONFIG_HOST + '/kiosk/' + kioskId + '/config',
      success: function (clubConfig) {
        if (!clubConfig || typeof data === 'string' || clubConfig === 'kioskId not found' || !clubConfig.amenities) {
          setTimeout(function () {
            pfKioskServices.getConfig(kioskId, onSuccess, onFail);
          }, 10000);
        } else {
          store(kioskId, clubConfig);
          onSuccess(clubConfig);
        }
      },
      error: function (jqXHR, textStatus, errorThrown) {
        onFail(jqXHR, textStatus, errorThrown);
      }
    });
  },

  /**
   * Posts request for member skin type waiver reset
   * @param memberbarcode {string}
   * @param clubcode {string}
   * @param waiverId {string}
   * @param onSuccess {function}
   * @param onFail {function}
   */
  resetWaiver: function (memberbarcode, clubcode, waiverId,waiverTitle, onSuccess, onFail) {
    $.ajax({
      method: 'POST',
      dataType: "text",
      url: config.WAIVERRESET_HOST + '&barcode='+memberbarcode+'&clubCode='+clubcode+'&waiverId='+waiverId+'&waiverTitle='+waiverTitle,
      success: function (res) {
        if (onSuccess) {
          onSuccess({
            response: res,
            waiverId:waiverId
          });
        }
      },
      error: function (jqXHR, textStatus, errorThrown) {
        if (onFail) {
          onFail(jqXHR, textStatus, errorThrown);
        }
      }
    });
  },

  /**
   * Post to table storage log
   * @param event {object}
   * @param event.name {string}
   * @param event.properties {object}
   * @param event.metrics {object}
   */
  trackEvent: function (event) {
    if (typeof appInsights !== 'undefined') {
      appInsights.trackEvent(event.name, event.properties, event.metrics);
    }
    else {
      debug('Kiosk:tracking')(JSON.stringify({
        name: event.name,
        properties: event.properties,
        metrics: event.metrics
      }));
    }
  }
};

/**
 * general jQuery Ajax failure function.
 * Checks ajax settings for retry {object} and will retry the xhr request
 * starting at retry.interval ms and increase over time to a maximum of retry.maximum sec.
 */
$(document).ajaxError(function (event, jqxhr, settings) {
  if (settings.retry) {
    if (!settings.attempts) {
      settings.attempts = 1;
    } else {
      settings.attempts++;
    }
    if (settings.attempts * settings.retry.interval < settings.retry.maximum) {
      var retryIntervual = settings.attempts * settings.retry.interval;
      console.log(pfFetchTranslation('siteLoad.connectionError', [retryIntervual]));
      setTimeout(function () {
        $.ajax(settings);
      }, settings.attempts * settings.retry.interval);
    } else {
      console.log(pfFetchTranslation('siteLoad.connectionErrorMax', [settings.retry.maximum]));
      setTimeout(function () {
        console.log(moment().format());
        $.ajax(settings);
      }, 60000);
    }
  }
});


if (typeof module === 'object' && module.exports) {
  module.exports = pfKioskServices;
}

/**
 * General jQuery Ajax success function.
 * Removes the error dialog (if it's open) on a successful response.
 */
$(document).ajaxSuccess(function (event, jqxhr, settings) {
  if ($('#errorDialog').hasClass('in, unauthorized')) {
    $('#errorDialog').modal('hide');
  }
});
